import React, { useState, useEffect, useRef } from "react";
import {
  MyWindow,
  HarmonyError
} from "../../types/thirdParty/harmony/ColonialEnrollmentApp";
import { fetchHarmonyEnrollmentTokenForPackage, updateElectionResults } from "../../apiCalls/Harmony/harmonyApiCalls";
import { useParams, useHistory } from "react-router-dom";
import { HarmonyProductGroup } from "../../types/thirdParty/harmony/HarmonyProductGroup";
import { EnrollmentPackageId } from "../../types/api/primaryKeys/PrimaryKey";
import { LoadingBlock } from "../LoadingBlock";
import { ReactComponent as Logo } from "./colonial.svg";
import "./Harmony.scss";
import { useWolfTracsState } from "../../hooks/redux/useWolfTracsState";
import { stepNumberHelper } from "../../utils/StepNumberHelper";
import {
  Package,
  PackageStep,
  PackageStepType
} from "../../types/api/enrollmentPackages/Package";
import { Nullable } from "../../types/custom/Nullable";
import { fetchUpdatePackageProgress } from "../../actions/EnrollmentActions";
import { useReduxDispatch } from "../../hooks/redux/useReduxDispatch";
import ErrorPage from "./ErrorPage";
import { onHarmonyInit, onSuccessCallBack, onWidgetRender, fetchUpdateHarmonyProgress, fetchingToken } from "../../actions/HarmonyActions";
import { FaAngleDoubleRight } from "react-icons/fa";
import { IconContext } from "react-icons";
import { permissionsService } from "../../utils/PermissionService";
import { UserRole } from "../../types/enums/UserRole";
import { UserDetail } from "../../types/api/users/UserDetail";

type ErrorStatus = {
  showError: boolean;
  sessionError: boolean;
  errorMsg: Nullable<string>;
}

type Props = {
  packageId: EnrollmentPackageId;
};

const harmonyURL = process.env.REACT_APP_HARMONY_API_ENDPOINT;
/*
 * This is the harmony integration component.
 */
const Harmony: React.FC<Props> = props => {
  const widgetWrapper = useRef<HTMLDivElement>(null);
  const { packageId } = props;
  const wrapperId = "widgetTest";
  const [scriptLoaded, harmonyScriptError] = useScript(`${harmonyURL}`);
  const myWindow: MyWindow = window;
  const { productGroup } = useParams() as { productGroup: HarmonyProductGroup }; //productGroup of harmony e.g. Dental, Life
  const [isCallBackReady, setCallBack] = useState(false); //callback function so that harmony tells component when its ready
  const [showLoader, setLoader] = useState(true);
  const [harmonyErrorStatus, setErrorStatus] = useState<ErrorStatus>({ showError: false, sessionError: false, errorMsg: null });
  const [prevProduct, setPrevious] = useState<Nullable<HarmonyProductGroup>>(null);
  const { activePackage } = useWolfTracsState(s => s.packages);
  const [isUserEligible, setUserEligibility] = useState(false);
  const [userInitialized, setUserInit] = useState(false);
  const history = useHistory();
  const dispatch = useReduxDispatch();
  //Harmony State
  //I think these can be all converted to local except isFetching... just as a side note. will revisit
  const { isHarmonyDirty, harmonyProductGroup, enrollmentToken, isFetching } = useWolfTracsState(s => s.harmony);
  //Ref to make these object mutable as these refs will be used only on unmount
  const productRef = useRef(harmonyProductGroup);
  const isDirty = useRef(isHarmonyDirty);
  const currentToken = useRef(enrollmentToken);
  const isHarmonyElectionUpdateCallInProgress =  useRef(false);

  // Fetching Enrollment Token
  useEffect(() => {
    const getToken = async () => {
      try {
        dispatch(fetchingToken());
        const result = await fetchHarmonyEnrollmentTokenForPackage(packageId);
        dispatch(onHarmonyInit(result.enrollmentTokenId));
      } catch (err) {
        dispatch(onHarmonyInit(null));
        handleSessionExpire();
        setLoader(false);
      }
    };
    if (!isFetching) {
      getToken();
    }
  }, [packageId]);


  //This method checks the user's eligibility for Colonial Life products
  //This is required because Colonial Life's legal team has expressed the need to limit the usage of the widget to Enrollment or Self-Enrollee
  //To handle this, we had to check if the user is an employee(self-enrollee), enrollmentassistant, or we check email address in case a user who has role of admin is also signing up
  //Because in that case, the admin is also the employee thus, employee (self-enrollee)
  useEffect(() => {
    const getUserInfo = async (currentPackage: Package) => {
      const user = await permissionsService.getCurrentUser();
      if (user !== null) {
        const isSystemAdmin = await permissionsService.currentUserHasOnlyThisRole(UserRole.System_Admin);
        const isEnrollmentAssistant = await permissionsService.currentUserHasOnlyThisRole(UserRole.Enrollment_Assistant);
        const isEmployee = await permissionsService.currentUserHasOnlyThisRole(UserRole.Employee);
        if (isSystemAdmin || isEnrollmentAssistant || isEmployee || checkSelfEnrollee(user, currentPackage)) {
          setUserEligibility(true);
        } else {
          setLoader(false);
        }
        setUserInit(true);
      }
    };

    //check current user's email address and make sure it matches with enrollment package email address to check if user is an admin doing self-enroll
    const checkSelfEnrollee = (currentUser: UserDetail, activePackage: Package) => Boolean(activePackage.emailAddress) && activePackage.emailAddress === currentUser.email;

    //check once we have package data
    if (activePackage.data) {
      getUserInfo(activePackage.data);
    }
  }, [activePackage]);

  //on unmount, check if harmony needs to be updated in case user uses side nav and doesn't use CTA (Summary: Non-happy flow)
  useEffect(() => () => {
    //unmount only
    if (isDirty.current && productRef.current && currentToken.current) {
      dispatch(fetchUpdateHarmonyProgress(packageId, productRef.current, currentToken.current));
    }
  }, []);

  //Initializing Harmony when Enrollment Token is available and the 3rdparty script is ready
  useEffect(() => {
    //renders on init and prop switch (e.g. Dental to TermLife)
    if (userInitialized && isUserEligible && isCallBackReady && enrollmentToken && activePackage.data && prevProduct !== productGroup && !isFetching) {
      //when switching between one harmony to another product, update in case of user's manual nav
      if (prevProduct && isDirty.current) {
        setErrorStatus({ showError: false, sessionError: false, errorMsg: null });
        dispatch(fetchUpdateHarmonyProgress(packageId, prevProduct, enrollmentToken));
      }
      initializeHarmony(enrollmentToken, activePackage.data, productGroup);
      currentToken.current = enrollmentToken;
      setLoader(false);
    }
    //even if the user is not ineligible, in case user has existing policy update the products as stepping through
    if (userInitialized && !isUserEligible && enrollmentToken) {
      //when switching between one harmony to another product, update in case of user's manual nav
      if (prevProduct !== productGroup && prevProduct && isDirty.current) {
        dispatch(fetchUpdateHarmonyProgress(packageId, prevProduct, enrollmentToken));
        isDirty.current = false;
      }
      currentToken.current = enrollmentToken;
      if (prevProduct === productGroup) {
        productRef.current = productGroup;
      } else {
        setPrevious(productGroup);
        isDirty.current = true;
      }
    }
  }, [isCallBackReady, enrollmentToken, activePackage.data, productGroup, isFetching, isUserEligible, userInitialized]);

  /*
   * Function to initialize harmony.
   * first registering security client
   * second fetching the related data service with javascript api
   * lastly, calling their widget using productGroup
   */
  const initializeHarmony = (
    harmonyToken: string,
    currentPackage: Package,
    harmonyProduct: HarmonyProductGroup
  ) => {
    const colonialEnrollmentApp = myWindow.colonialEnrollmentApp!;

    //onComplete function for the widget
    const onPageForward = async (
      currentPackage: Package,
      product: HarmonyProductGroup
    ) => {
      if(isHarmonyElectionUpdateCallInProgress.current) {
        return;
      }

			try {
        isHarmonyElectionUpdateCallInProgress.current = true; //this code is done to prevent duplicate calls from firing from Colonial Systems
        const currentStepNumber = stepNumberHelper.getNumberBasedOnProduct(currentPackage, product);
				await updateElectionResults(packageId, harmonyToken, harmonyProduct);
				dispatch(onSuccessCallBack());
				isDirty.current = false;
				await dispatch(
					fetchUpdatePackageProgress(packageId, currentStepNumber, true)
				);
				history.push(getNextStepRoute(currentPackage, currentStepNumber));
			} catch {
				//hard stop
				handleSessionExpire();
			} finally {
        isHarmonyElectionUpdateCallInProgress.current = false;
      }
    };

    //Can this be skipped??
    const processEnrollment = () => {
      const enrollmentSession = colonialEnrollmentApp.enrollmentSession();
      enrollmentSession.onSuccess(() => processWidget(harmonyProduct));
      enrollmentSession.onFail(handleFail);
      enrollmentSession.get();
    };

    const processWidget = (product: HarmonyProductGroup) => {
      const colonialWidget = colonialEnrollmentApp.enrollmentWidget();
      colonialWidget.onComplete(() =>
        onPageForward(currentPackage, harmonyProduct)
      );
      colonialWidget.onFail(handleFail);
      //in case we demounted the component
      if (widgetWrapper.current && !harmonyScriptError) {
        if (!isHarmonyDirty) {
          dispatch(onWidgetRender(product));
          productRef.current = product;
          isDirty.current = true;
        }
        colonialWidget.renderToElementId(wrapperId);
        colonialWidget.render(productGroup);
        setPrevious(product);
      }
    };

    const colonialSecurity = colonialEnrollmentApp.security();
    colonialSecurity.onFail(handleSessionExpire);
    colonialSecurity.onSuccess(processEnrollment);
    colonialSecurity.registerEnrollmentToken(harmonyToken);
  };

  //getting the next step
  const getNextPage = (
    currPackage: Package,
    num: Nullable<number>,
    direction: number
  ): Nullable<PackageStep> => {
    if (num) {
      const result = currPackage.steps.find(
        step => step.number === num + direction
      );
      return result ?? null;
    } else {
      return null;
    }
  };

  //generating the next step url
  const getNextStepRoute = (
    currPackage: Package,
    currentStep: Nullable<number>
  ) => {
    const nextStep = getNextPage(currPackage, currentStep, 1);
    if (nextStep) {
      if (
        nextStep.type === PackageStepType.PlanType &&
        nextStep.relatedEntityId
      ) {
        if (nextStep.harmonyProductGroup) {
          return (
            "/packages/" +
            currPackage.enrollmentPackageId +
            "/harmony/" +
            nextStep.harmonyProductGroup
          );
        } else {
          return (
            "/packages/" +
            currPackage.enrollmentPackageId +
            "/plantypes/" +
            nextStep.relatedEntityId
          );
        }
      } else {
        if (nextStep.type === PackageStepType.Insurability) {
          return (
            "/packages/" + currPackage.enrollmentPackageId + "/insurability"
          );
        } else {
          return "/packages/" + currPackage.enrollmentPackageId + "/summary";
        }
      }
    } else {
      // if cannot determine next step, return current step
      return (
        "/packages/" +
        currPackage.enrollmentPackageId +
        "/harmony/" +
        productGroup
      );
    }
  };

  const handleFail = (err: HarmonyError) => {
    //TODO: Add code to handle types of errors
    if (err.status === 500) {
      setErrorStatus({ showError: true, sessionError: false, errorMsg: err.statusText });
    } else {
      setErrorStatus({ showError: true, sessionError: false, errorMsg: null });
    }
  };

  const handleSessionExpire = (err?: HarmonyError) => {
    setErrorStatus({ showError: true, sessionError: true, errorMsg: null });
  };

  const readyCallBack = () => {
    setCallBack(true);
  };

  const continueToNext = async () => {
    const currentPackage = activePackage.data;
    if (currentPackage) {
      const currentStepNumber = stepNumberHelper.getNumberBasedOnProduct(
        currentPackage,
        productGroup
      );
      dispatch(onSuccessCallBack());
      isDirty.current = true;
      if (!isUserEligible) {
        currentToken.current = enrollmentToken;
        productRef.current = productGroup;
        setPrevious(productGroup);
      }
      await dispatch(fetchUpdatePackageProgress(packageId, currentStepNumber, true));
      history.push(getNextStepRoute(currentPackage, currentStepNumber));
    }
    //else means "no next step could be determined because there is not package data"
  };
  myWindow.initializeColonialEnrollmentWidget = readyCallBack;

  return (
    <div>
      <div className="harmonyHeader">
        <div className="logoHolder">
          <Logo />
        </div>
        <div className="buttonHolder">
          <IconContext.Provider value={{ style: { marginBottom: '3px', marginLeft: '3px' } }}>
            <button type="submit" className="btn btn-primary float-right" onClick={continueToNext}>Continue to Next <FaAngleDoubleRight /></button>
          </IconContext.Provider>
        </div>
      </div>
      {showLoader && <LoadingBlock />}
      {!showLoader && !isUserEligible &&
        <div className="alert alert-danger" role="alert">
          <h3>Not Authorized</h3>
          <p className="no-bot">Only employees and trusted agents are allowed to add or change this Colonial Life policy. If you feel like you are seeing this message in error, please contact your WolfTracs administrator.</p>
        </div>
      }
      {scriptLoaded && !harmonyScriptError && !harmonyErrorStatus.sessionError && <div id={wrapperId} ref={widgetWrapper}></div>}
      {isUserEligible && harmonyErrorStatus.showError && ErrorPage(harmonyErrorStatus.sessionError, harmonyErrorStatus.errorMsg)}
    </div>
  );
};

//A hook to test to load the script and make sure it does not duplicate itself.
//On Cleanup, removes the script from the dom again so it can reinitialize
const cachedScripts = new Set<string>();
const useScript = (src: string) => {
  const [state, setState] = useState({
    loaded: false,
    error: false
  });

  useEffect(() => {
    if (cachedScripts.has(src)) {
      setState({
        loaded: true,
        error: false
      });
    } else {
      cachedScripts.add(src);

      const script = document.createElement("script");
      script.id = "specialScript";
      script.src = src;
      script.async = true;

      const onScriptLoad = () => {
        setState({
          loaded: true,
          error: false
        });
      };

      const onScriptError = () => {
        if (cachedScripts.has(src)) {
          cachedScripts.delete(src);
        }
        script.remove();

        setState({
          loaded: true,
          error: true
        });
      };

      script.addEventListener("load", onScriptLoad);
      script.addEventListener("error", onScriptError);

      document.body.appendChild(script);

      return () => {
        script.removeEventListener("load", onScriptLoad);
        script.removeEventListener("error", onScriptError);
        if (document.getElementById(script.id)) {
          document.body.removeChild(script);
        }
        cachedScripts.delete(src);
      };
    }
  }, [src]);

  return [state.loaded, state.error, state];
};

export default Harmony;
